import React from 'react';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import { Card, Tooltip } from '@kaleyra/emmy';
import moment from 'moment';

import {
	// ChannelOptions,
	Instances,
	TimeDropDownOptions,
	CallbackType,
} from '../../../../../constants';
import { ChannelType, DefaultPropertyType, FilterType, OverviewType } from '../../../../../interface';

import SeeMoreModal, {
	BaseSeeMorePropsType,
} from '../../../templates/SeeMoreModal';
import TitleSearchTable from '../../../templates/TitleSearchTable';
import TableFactory from '../../../templates/TableTemplate';
import WidgetFactory from '../../../containers/Overview/Widget';

import Header from '../Header';

import Styles from './index.module.scss';
import { debounce } from 'lodash';
import infoIcon from '../../../../../assets/InfoIconGray.svg'

export type MapStateType = {
	filters: FilterType;
	smsOverview?: OverviewType[];
};

export type DispatchPropsType = {
	onFilterChange: (filters: FilterType) => void;
	onSearchChange: (instanceId: Instances, searchStr: string) => void;
};

export type FilterProp = {
	setFilterKey?: (key: string) => void;
}

const [start, end] = TimeDropDownOptions[3].value as Array<string>;

const initFilter = {
	timerange: {
		start_time: start,
		end_time: end,
	},
};

const SMSWidget = WidgetFactory(Instances.smsOverview);
const WhatsAppWidget = WidgetFactory(Instances.whatsappOverview);
const SMSTable = TableFactory(Instances.smsOverviewSeeMore);
const WhatsappTable = TableFactory(Instances.whatsappOverviewSeeMore);

type PropsType = MapStateType & BaseSeeMorePropsType & DispatchPropsType;

const TabDetailMap = {
	[ChannelType.sms]: Instances.smsOverviewSeeMore,
	[ChannelType.whatsApp]: Instances.whatsappOverviewSeeMore,
};

type RenderBodyPropsType = {
	currentTab: string;
}

const TabBody: React.FC<RenderBodyPropsType & MapStateType & DispatchPropsType> = ({
	filters,
	onSearchChange,
	currentTab,

}) => {

	const { t } = useTranslation();

	const getChannel = (currentTabVal: string) => {
		let widget = null;
		switch (currentTabVal) {
			case ChannelType.sms:
				widget = <SMSWidget channel={currentTabVal as ChannelType} />;
				break;
			case ChannelType.whatsApp:
				widget = <WhatsAppWidget channel={currentTabVal as ChannelType} />;
				break;
			default:
				widget = <></>;
		}
		return <Card showHoverBorder={false}>{widget}</Card>;
	};


	const getHeader = () => {
			return (
				<div>{t("KLR02609")}
					<Tooltip
						title={t("KLR02612")}
						children={<img src={infoIcon} className={Styles.infoIcon} />}
					/>
				</div>
			)
	
	}
	const getTable = (currentTabVal: string) => {
		switch (currentTabVal) {
			case ChannelType.sms:
				return (
					<SMSTable
						searchTerm={'sender'}
						columns={[
							{
								header: t('overviewWidgetSMSSenderId'),
								accessor: 'sender',

							},
							{
								header: t("KLR02585"),
								accessor: 'overall.sent',
								alignHeader: 'right',
							},
							{
								header: t('overviewWidgetTotalMKT'),
								accessor: 'route.mkt',
								alignHeader: 'right',
							},
							{
								header: t('overviewWidgetTotalTXN'),
								accessor: 'route.txn',
								alignHeader: 'right',
							},
							{
								header: t('overviewWidgetTotalOTP'),
								accessor: 'route.otp',
								alignHeader: 'right',
							},
							{
								header: t('overviewWidgetTotalSMSDelivered'),
								accessor: 'overall.delivered',
								alignHeader: 'right',
							},
							{
								header: t("KLR02597"),
								accessor: 'overall.sent_no_dlr',
								alignHeader: 'right',
							},
							{
								header:getHeader(),
								accessor: 'overall.failed',
								alignHeader: 'right',
							},
						]}
						filters={isEmpty(filters) ? initFilter : filters}
					/>
				);
			case ChannelType.whatsApp:
				return (
					<WhatsappTable
						searchTerm={'number'}
						columns={[
							{
								header: t('overviewWidgetWhatsAppNumber'),
								accessor: 'number',
							},
							{
								header: t('overviewWidgetWhatsAppSent'),
								accessor: 'sent',
								alignHeader: 'right',
							},
							{
								header: t('overviewWidgetWhatsAppTemplate'),
								accessor: 'template',
								alignHeader: 'right',
							},
							{
								header: t('overviewWidgetWhatsAppSession'),
								accessor: 'session',
								alignHeader: 'right',
							},
							{
								header: t(
									'overviewWidgetWhatsAppIncomingMessage'
								),
								accessor: 'incoming',
								alignHeader: 'right',
							},
							{
								header: t('overviewWidgetWhatsAppDelivered'),
								accessor: 'delivered',
								alignHeader: 'right',
							},
							{
								header: t('overviewWidgetWhatsAppFailed'),
								accessor: 'failed',
								alignHeader: 'right',
							},
						]}
						filters={isEmpty(filters) ? initFilter : filters}
					/>
				);
			default:
				return null;
		}
	};

	const { timerange } = filters;
	const { start_time: startVal, end_time: endVal } = timerange || {};

	const onSearch = (searchStr: string = '') => {
		onSearchChange((TabDetailMap as any)[currentTab], searchStr);
	};

	const debouncedChange = React.useCallback<CallbackType>(
		debounce((value: any = "") => onSearch(value), 400),
		[currentTab]
	);

	return (
		<>
			{getChannel(currentTab || ChannelType.sms)}
			<div className={Styles.tableContainer}>
				<TitleSearchTable
					title={`${t(
						`channelType${currentTab || ChannelType.sms}`
					)} Overview - ${moment.unix(+startVal).format(
						'DD MMM YYYY'
					)} to ${moment.unix(+endVal).format('DD MMM YYYY')}`}
					onChange={debouncedChange}
					currentTab={currentTab}
				>
					{getTable(currentTab || ChannelType.sms)}
				</TitleSearchTable>
			</div>
		</>
	);
}

const OverviewSeeMore: React.FC<PropsType & FilterProp> = ({
	filters,
	onFilterChange,
	onSearchChange,
	smsOverview,
	setFilterKey,
	...props
}) => {

	const [tab, setTab] = React.useState<string>(ChannelType.sms);

	const { t } = useTranslation();

	const customChannelOptions = (): Array<DefaultPropertyType> => {
		let channels: Array<DefaultPropertyType> = [];
		channels.push({
			label: 'SMS',
			value: 'sms'
		})
		// if (smsOverview && smsOverview.length > 0 && smsOverview[0].channel_status.whatsapp) {
		// 	channels.push({
		// 		label: 'WhatsApp',
		// 		value: 'whatsapp'
		// 	})
		// }
		return channels;
	}

	return (
		<SeeMoreModal
			header={
				<Header
					filters={filters}
					onFilterChange={onFilterChange}
					showDownload
					instanceId={(TabDetailMap as any)[tab]}
					message={t(`overview${tab}ReportDownload`)}
					setFilterKey={(key: string) => setFilterKey && setFilterKey(key)}
				/>
			}
			getBody={(currentTab: string) => <TabBody currentTab={currentTab} onSearchChange={onSearchChange} onFilterChange={onFilterChange} filters={filters} />}
			tabs={customChannelOptions()}
			onTabClick={setTab}
			{...props}
		/>
	);
};

export default OverviewSeeMore;